/**
 * Branding config validation schemas.
 * Defines visual styling and brand customization.
 */
import { z } from "zod";
// ============================================================================
// Common Patterns
// ============================================================================
/** Hex color pattern */
const hexColorPattern = /^#[0-9A-Fa-f]{6}$/;
// ============================================================================
// Branding Colors
// ============================================================================
/** Branding color scheme */
export const BrandingColorsSchema = z
    .object({
    primary: z.string().regex(hexColorPattern, "Must be a valid hex color"),
    accent: z.string().regex(hexColorPattern, "Must be a valid hex color"),
    background: z.string().regex(hexColorPattern, "Must be a valid hex color"),
    surface: z.string().regex(hexColorPattern, "Must be a valid hex color"),
    textPrimary: z.string().regex(hexColorPattern, "Must be a valid hex color"),
    textSecondary: z.string().regex(hexColorPattern, "Must be a valid hex color"),
    error: z.string().regex(hexColorPattern, "Must be a valid hex color").optional(),
    success: z.string().regex(hexColorPattern, "Must be a valid hex color").optional(),
    warning: z.string().regex(hexColorPattern, "Must be a valid hex color").optional(),
})
    .strict();
// ============================================================================
// Branding Fonts
// ============================================================================
/** Branding font configuration */
export const BrandingFontsSchema = z
    .object({
    heading: z.string().min(1).max(100).optional(),
    body: z.string().min(1).max(100).optional(),
})
    .strict();
// ============================================================================
// Branding Configuration
// ============================================================================
/** Branding configuration */
export const BrandingConfigSchema = z
    .object({
    verticalKey: z.string().min(1).optional(),
    version: z.number().int().min(1),
    logoUrl: z.string().url().optional(),
    faviconUrl: z.string().url().optional(),
    colors: BrandingColorsSchema,
    fonts: BrandingFontsSchema.optional(),
    customCss: z.string().max(50000).optional(),
})
    .strict();
// ============================================================================
// Theme Variants
// ============================================================================
/** Theme mode */
export const ThemeModeSchema = z.enum(["light", "dark", "system"]);
/** Extended branding with theme support */
export const ThemeBrandingConfigSchema = BrandingConfigSchema.extend({
    mode: ThemeModeSchema.optional(),
    darkColors: BrandingColorsSchema.optional(),
}).strict();
// ============================================================================
// Logo Configuration
// ============================================================================
/** Logo size variants */
export const LogoSizeSchema = z.enum(["small", "medium", "large"]);
/** Logo configuration */
export const LogoConfigSchema = z
    .object({
    url: z.string().url(),
    altText: z.string().min(1).max(100),
    width: z.number().int().min(1).max(1000).optional(),
    height: z.number().int().min(1).max(1000).optional(),
    size: LogoSizeSchema.optional(),
})
    .strict();
